import { AlertCircleIcon, CheckIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Button, Display, formatDate, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { Badges } from '@prophecy/ui-v3/Badges';
import { pluralize } from '@prophecy/utils/string';

import { BarLabel, BarPercent, CapacityBanner, CapacityCard, CardPadding, Dot, UsedSummary } from './styled';
import { CapacityStatus, SapUsageDashboardData } from './types';
import { UsageBar } from './UsageBar';
import { clampPercent, formatCredits } from './utils';

const STATUS_META: Record<CapacityStatus, { label: string; tone: `${BaseColor}`; color: string }> = {
  onTrack: { label: 'On Track', tone: BaseColor.success, color: theme.colors.success500 },
  runningOutFast: { label: 'Running out fast', tone: BaseColor.warning, color: theme.colors.warning500 },
  exhausted: { label: 'Capacity exhausted', tone: BaseColor.error, color: theme.colors.error500 }
};

const BAR_COLOR: Record<CapacityStatus, string> = {
  onTrack: theme.colors.brand500,
  runningOutFast: theme.colors.warning600,
  exhausted: theme.colors.error500
};

function LabelledBar({ label, percent, color }: { label: string; percent: number; color: string }) {
  const filledPercent = clampPercent(percent);

  return (
    <Stack direction='horizontal' alignY='center' gap={theme.spaces.x20}>
      <BarLabel>
        <Text level='xs' tone={theme.textColor.secondary}>
          {label}
        </Text>
      </BarLabel>
      <StackItem grow='1'>
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x12}>
          <StackItem grow='1'>
            <UsageBar segments={[{ value: filledPercent, color }]} total={100} />
          </StackItem>
          <BarPercent>
            <Text level='sm' weight={theme.fontWeight.medium} tone={theme.textColor.secondary}>
              {Math.round(filledPercent)}%
            </Text>
          </BarPercent>
        </Stack>
      </StackItem>
    </Stack>
  );
}

function BannerCopy({ data }: { data: SapUsageDashboardData }) {
  const { status, projection, totalCreditsAllocated } = data;

  if (status === 'exhausted') {
    return (
      <>
        <StackItem grow='1'>
          <Text level='sm' tone={theme.textColor.secondary}>
            Your capacity units are fully consumed - <b>contact your account team</b> to restore capacity. New pipeline
            runs may be paused.
          </Text>
        </StackItem>
        {projection && (
          <Text level='sm' tone={theme.textColor.tertiary}>
            {projection.daysBeforeCycleEnds} {pluralize(projection.daysBeforeCycleEnds, 'day')} remain on contract
          </Text>
        )}
      </>
    );
  }

  if (status === 'runningOutFast' && projection) {
    return (
      <>
        <StackItem grow='1'>
          <Text level='sm' tone={theme.textColor.secondary}>
            At your current pace of <b>{formatCredits(projection.burnRatePerDay)} CU/day</b>, credits <b>run out</b>
            {projection.exhaustionDate ? ` around ${formatDate(projection.exhaustionDate, 'dd MMM, yyyy')}` : ''}
          </Text>
        </StackItem>
        <Text level='sm' tone={theme.textColor.tertiary}>
          {projection.daysLeftAtCurrentPace !== undefined &&
            `~${projection.daysLeftAtCurrentPace} ${pluralize(projection.daysLeftAtCurrentPace, 'day')} left · `}
          {projection.daysBeforeCycleEnds} {pluralize(projection.daysBeforeCycleEnds, 'day')} before contract ends
        </Text>
      </>
    );
  }

  return (
    <>
      <StackItem grow='1'>
        <Text level='sm' tone={theme.textColor.secondary}>
          Your current usage pattern indicates that you will not run out of capacity in your Contract Year.
        </Text>
      </StackItem>
      {projection && (
        <Text level='sm' tone={theme.textColor.tertiary}>
          Projected year-end usage: ~{formatCredits(projection.projectedCycleEndCredits)} of{' '}
          {formatCredits(totalCreditsAllocated)} CU
        </Text>
      )}
    </>
  );
}

export function CapacityConsumptionCard({
  data,
  onRequestCapacity
}: {
  data: SapUsageDashboardData;
  onRequestCapacity?: () => void;
}) {
  const { status, usedPercent, creditsConsumed, totalCreditsAllocated, cycleElapsedPercent } = data;
  const statusMeta = STATUS_META[status];
  const barColor = BAR_COLOR[status];

  return (
    <CapacityCard $status={status} data-test-id='sap-usage-capacity-card'>
      <CardPadding gap={theme.spaces.x20}>
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x12}>
          <Text level='md' weight={theme.fontWeight.medium}>
            Capacity Consumption ({formatCredits(creditsConsumed)} of {formatCredits(totalCreditsAllocated)} CU)
          </Text>
          <Badges
            size='m'
            variant='pillOutline'
            tone={statusMeta.tone}
            iconPlacement='left'
            icon={<Dot $color={statusMeta.color} $size={theme.sizes.x6} />}
            data-test-id='sap-usage-capacity-status-badge'>
            {statusMeta.label}
          </Badges>
          <StackItem grow='1' />
          {onRequestCapacity && (
            <Button
              size='s'
              variant='primary'
              onClick={onRequestCapacity}
              data-test-id='sap-usage-request-capacity-button'>
              Request More Capacity
            </Button>
          )}
        </Stack>

        <Stack direction='horizontal' gap={theme.spaces.x20} alignY='center'>
          <UsedSummary gap={theme.spaces.x4}>
            <Display level='xs' weight={theme.fontWeight.bold}>
              {Math.round(clampPercent(usedPercent))}%
            </Display>
            <Text level='xs' tone={theme.textColor.tertiary}>
              Used
            </Text>
          </UsedSummary>
          <StackItem grow='1'>
            <Stack gap={theme.spaces.x8}>
              <LabelledBar label='Capacity Consumed' percent={usedPercent} color={barColor} />
              <LabelledBar label='Contract Year' percent={cycleElapsedPercent} color={barColor} />
            </Stack>
          </StackItem>
        </Stack>
      </CardPadding>

      <CapacityBanner $status={status}>
        <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
          {status === 'onTrack' ? (
            <CheckIcon type='default' size='s' color={theme.colors.success600} />
          ) : (
            <AlertCircleIcon
              type='default'
              size='s'
              color={status === 'exhausted' ? theme.colors.error600 : theme.colors.warning600}
            />
          )}
          <BannerCopy data={data} />
        </Stack>
      </CapacityBanner>
    </CapacityCard>
  );
}
